/**
 * Camera-based barcode scanning for scan mode.
 *
 * TODO: The searchbar barcode button now links directly to /pages/scanner
 * (see snippets/predictive-search.liquid), so the #scan-camera-button entry
 * point and the scan-camera-overlay flow are unreachable from the header.
 * Clean up the overlay snippet, the overlay-bound handlers in this file, and
 * the `search__scan-camera` styles if no other entry point remains.
 */
(function() {
  'use strict';

  const button = document.getElementById('scan-camera-button');
  const overlay = document.getElementById('scan-camera-overlay');

  if (!overlay) return;

  const SV = window.IsalbiScanSessionStatuses || {
    ACCEPTED: 'accepted',
    RESOLVING: 'resolving',
    RESOLVED: 'resolved',
    QUEUED: 'queued',
    SENDING: 'sending',
    COMMITTED: 'committed',
    FAILED: 'failed'
  };
  const sessionStore = window.IsalbiScanSessionStore
    ? (window.__isalbiScanSessionStore =
        window.__isalbiScanSessionStore || new window.IsalbiScanSessionStore())
    : null;

  const storeHeader = document.querySelector('store-header');
  const video = document.getElementById('scan-camera-video');
  const statusEl = document.getElementById('scan-camera-status');
  const errorEl = document.getElementById('scan-camera-error');
  const fallbackEl = document.getElementById('scan-camera-fallback');
  const stepEl = document.getElementById('scan-camera-step');
  const closeBtn = overlay.querySelector('.scan-camera-close');
  const flashEl = document.getElementById('scan-camera-flash');
  const footerEl = overlay.querySelector('.scan-camera-footer');

  const barcodeIndexUrl = overlay.dataset.barcodeIndexUrl || '';
  const resolveUrl = overlay.dataset.resolveUrl || '';
  const shopDomain = overlay.dataset.shopDomain || '';
  const zxingUrl = overlay.dataset.zxingUrl || '';
  const barcodePolyfillUrl = overlay.dataset.barcodePolyfillUrl || '';
  const zbarWasmUrl = overlay.dataset.zbarWasmUrl || '';
  const searchUrl = overlay.dataset.searchUrl || '/search';
  const serverCartEnabled = String(overlay.dataset.serverCartEnabled || '').toLowerCase() === 'true';

  const getNumberSetting = (value, fallback) => {
    if (value == null || value === '') return fallback;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const ALLOWED_FORMAT_KEYS = ['upc_a', 'ean_13', 'ean_8', 'itf_14', 'code_128'];

  const parseFormatList = (value, fallback) => {
    if (!value) return fallback;
    const parsed = String(value)
      .split(',')
      .map((entry) => entry.trim().toLowerCase())
      .filter(Boolean)
      .filter((entry) => ALLOWED_FORMAT_KEYS.includes(entry));
    return parsed.length ? parsed : fallback;
  };

  const CACHE_KEY = 'isalbi_barcode_cache_v1';
  const CACHE_TTL = 24 * 60 * 60 * 1000;
  const COOLDOWN_MS = getNumberSetting(overlay.dataset.scanCooldownMs, 2000);
  const CART_MIN_DELAY_MS = 0;
  const CART_JITTER_MS = 1000;
  const CART_BACKOFF_BASE_MS = 800;
  const CART_BATCH_WINDOW_SERVER_MS = 900;
  const CART_BATCH_MAX = 12;
  const CART_TRAILING_WAIT_MS = 500;
  const THEME_SYNC_DEBOUNCE_MS = getNumberSetting(overlay.dataset.themeSyncDebounceMs, 5000);
  const THEME_CART_TRAILING_WAIT_MS = getNumberSetting(overlay.dataset.themeCartTrailingWaitMs, 500);
  const THEME_CART_JITTER_MS = getNumberSetting(overlay.dataset.themeCartJitterMs, 600);
  const THEME_CART_BATCH_WINDOW_MS = getNumberSetting(overlay.dataset.themeCartBatchWindowMs, 3000);
  const CART_ID_TTL_MS = 0;//6 * 60 * 60 * 1000;
  const CART_RESET_COOLDOWN_MS = 0;
  const RESUME_STORAGE_KEY = 'scanCamera.resume';
  const RESUME_RETURN_KEY = 'scanCamera.returnUrl';
  const RESUME_QUEUE_KEY = 'scanCamera.queue';
  const RESUME_PENDING_KEY = 'scanCamera.pendingAdds';
  const SERVER_CART_STORAGE_KEY = 'scanCamera.cartId';
  const SERVER_CART_CREATED_KEY = 'scanCamera.cartIdCreatedAt';
  const CART_RESET_SESSION_KEY = 'scanCamera.cartResetOnce';
  const THEME_CART_CACHE_KEY = 'scanCamera.themeCartSnapshot';
  const THEME_CART_SNAPSHOT_TTL_MS = 15000;
  const SCAN_CAMERA_DEBUG_ENABLED = false;
  const ERROR_BEEP_THROTTLE_MS = 900;
  const FRAME_INTERVAL_MS = 120;
  const ROI = { x: 0.12, y: 0.39, w: 0.76, h: 0.22 };
  const EXPANDED_ROI = { x: 0.08, y: 0.33, w: 0.84, h: 0.32 };
  const FULL_ROI = { x: 0, y: 0, w: 1, h: 1 };
  const IOS_ROI_EXPAND_AFTER_MS = 1200;
  const IOS_FULL_ROI_AFTER_MS = 2600;
  /**
   * Quick scan page only: short vertical band. Decoder uses naive buffer fractions (see drawRoiToCanvas);
   * reticle CSS is tuned to match when possible — adjust these + reticle together when mapping cover→buffer.
   */
  const SCANNER_PAGE_ROI = { x: 0.07, y: 0.45, w: 0.86, h: 0.1 };
  const SCANNER_PAGE_EXPANDED_ROI = { x: 0.06, y: 0.43, w: 0.88, h: 0.14 };
  const IOS_FRAME_INTERVAL_MS = 200;
  const IOS_GTIN_LENGTHS = [8, 12, 13, 14];
  const DEFAULT_GTIN_LENGTHS = [8, 12, 13, 14];
  // Prefer retail GTINs over logistics or internal codes.
  const FORMAT_PRIORITY = parseFormatList(
    overlay.dataset.scanFormatPriority,
    ['upc_a', 'ean_13', 'ean_8', 'itf_14', 'code_128']
  );
  const DETECTOR_FORMAT_KEYS = parseFormatList(
    overlay.dataset.scanDetectorFormats,
    ['upc_a', 'ean_13', 'ean_8', 'itf_14', 'code_128']
  );

  const DETECTOR_FORMATS = DETECTOR_FORMAT_KEYS.map((key) => {
    if (key === 'itf_14') return 'itf';
    if (key === 'code_128') return 'code_128';
    return key;
  });

  const state = {
    enabled: false,
    open: false,
    processing: false,
    closeRequested: false,
    decodingLocked: false,
    cooldownUntil: 0,
    queue: [],
    stream: null,
    rafId: null,
    lastFrameTime: 0,
    detector: null,
    zxingReader: null,
    audioCtx: null,
    isIOS: false,
    stepNumber: 1,
    openedAt: 0,
    lastDetectTime: 0,
    cache: new Map(),
    shardCache: new Map(),
    index: null,
    lastErrorBeepAt: 0,
    lastErrorBeepText: '',
    lastCartAddAt: 0,
    cartBackoffMs: 0,
    pendingAdds: [],
    batchTimer: null,
    batchInFlight: false,
    pendingThemeSync: [],
    themeSyncTimer: null,
    themeSyncInFlight: false,
    lastThemeSyncAt: 0,
    cartAdapter: 'theme',
    storefrontCart: null,
    drawerHiddenChildren: null,
    drawerContainer: null,
    drawerSummaryEl: null,
    lastStorefrontTotal: null,
    lastStorefrontLineIds: null,
    cartResetInFlight: false,
    lastCartResetAt: 0,
    lastThemeCart: null,
    backendDebugEl: null,
    backendDebugContentEl: null,
    lastKnownCartCount: null,
    securityHold: false,
    securityBanner: null,
    needsCartRefresh: false,
    cartRefreshTimer: null,
    inlineEmbedPlace: null,
    decodePipeline: '',
    decodeDrawPath: '',
    scanDebugInterval: null,
    decodeDebugFrame: 0,
    lastDecodeStripImg: ''
  };

  let headerSyncRaf = null;
  let headerResizeObserver = null;

  function syncStoreHeaderHeight() {
    if (!storeHeader) return;
    const rect = storeHeader.getBoundingClientRect();
    const height = Math.max(0, Math.round(rect.height));
    document.documentElement.style.setProperty('--store-header-height', `${height}px`);
  }

  function scheduleSyncStoreHeaderHeight() {
    if (headerSyncRaf) return;
    headerSyncRaf = requestAnimationFrame(() => {
      headerSyncRaf = null;
      syncStoreHeaderHeight();
    });
  }

  function initStoreHeaderHeightSync() {
    syncStoreHeaderHeight();

    window.addEventListener('resize', scheduleSyncStoreHeaderHeight, { passive: true });
    window.addEventListener('scroll', scheduleSyncStoreHeaderHeight, { passive: true });

    if (storeHeader && 'ResizeObserver' in window) {
      headerResizeObserver = new ResizeObserver(() => scheduleSyncStoreHeaderHeight());
      headerResizeObserver.observe(storeHeader);
    }
  }

  function setEnabled(enabled) {
    state.enabled = !!enabled;
    if (button) {
      button.disabled = !state.enabled;
      button.setAttribute('aria-hidden', state.enabled ? 'false' : 'true');
    }
    if (!state.enabled && state.open) {
      closeOverlay();
    }
  }

  function setStatus(text) {
    if (statusEl) statusEl.textContent = text;
  }

  function setStepNumber(step) {
    state.stepNumber = step;
    if (stepEl) stepEl.textContent = `Step ${step}`;
  }

  function setError(text, showFallback = false) {
    if (!errorEl) return;
    errorEl.textContent = text || '';
    errorEl.classList.toggle('scan-camera-error--visible', Boolean(text));
    if (fallbackEl) {
      fallbackEl.hidden = !showFallback;
      fallbackEl.href = searchUrl;
    }
    if (text) {
      const now = Date.now();
      const isNewMessage = String(text) !== state.lastErrorBeepText;
      const allowedByThrottle = now - state.lastErrorBeepAt > ERROR_BEEP_THROTTLE_MS;
      if (state.open && (isNewMessage || allowedByThrottle)) {
        state.lastErrorBeepAt = now;
        state.lastErrorBeepText = String(text);
        playErrorBeep();
      }
    }
  }


  function showToast(message, type = 'success') {
    const toast = document.createElement('div');
    toast.className = `scan-camera-toast scan-camera-toast--${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('scan-camera-toast--visible'));
    setTimeout(() => {
      toast.classList.remove('scan-camera-toast--visible');
      setTimeout(() => toast.remove(), 200);
    }, 2500);
  }

  function ensureBackendDebugEl() {
    if (!SCAN_CAMERA_DEBUG_ENABLED || state.backendDebugEl || !footerEl) return;
    const details = document.createElement('details');
    details.className = 'scan-camera-backend-debug';
    details.style.marginTop = '0.5rem';
    details.style.fontSize = '0.7rem';
    details.style.opacity = '0.8';
    details.style.background = 'rgba(17, 24, 39, 0.08)';
    details.style.borderRadius = '8px';
    details.style.padding = '0.4rem 0.6rem';

    const summary = document.createElement('summary');
    summary.textContent = 'Backend cart response';
    summary.style.cursor = 'pointer';
    summary.style.fontWeight = '600';
    summary.style.listStyle = 'none';
    summary.style.outline = 'none';

    const pre = document.createElement('pre');
    pre.style.margin = '0.35rem 0 0';
    pre.style.whiteSpace = 'pre-wrap';
    pre.style.wordBreak = 'break-word';
    pre.style.maxHeight = '160px';
    pre.style.overflow = 'auto';
    pre.style.fontSize = '0.7rem';
    pre.textContent = 'No response yet.';

    details.appendChild(summary);
    details.appendChild(pre);
    footerEl.appendChild(details);
    state.backendDebugEl = details;
    state.backendDebugContentEl = pre;
  }

  function setBackendDebug(payload) {
    if (!state.backendDebugContentEl) return;
    const summary = getScannerDebugSummary();
    const rawPayload = typeof payload === 'string' ? payload : JSON.stringify(payload, null, 2);
    const raw = JSON.stringify({ summary, payload: rawPayload }, null, 2);
    const capped = raw.length > 4000 ? `${raw.slice(0, 4000)}\n... (truncated)` : raw;
    state.backendDebugContentEl.textContent = capped || 'No response.';
  }

  function detectChallengeResponse(contentType, text) {
    const type = String(contentType || '').toLowerCase();
    const body = String(text || '').toLowerCase();
    if (type.includes('text/html')) return true;
    if (body.includes('cf-chl') || body.includes('cloudflare')) return true;
    return false;
  }

  function getThemeCartSnapshot() {
    try {
      const raw = sessionStorage.getItem(THEME_CART_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed?.cart) {
        return { cart: parsed.cart, savedAt: Number(parsed.savedAt) || 0 };
      }
    } catch (error) {
      console.warn('[ScanCamera] Theme cart snapshot load failed', error);
    }
    return null;
  }

  function loadThemeCartSnapshot() {
    const snapshot = getThemeCartSnapshot();
    if (snapshot?.cart) {
      state.lastThemeCart = snapshot.cart;
      return snapshot.cart;
    }
    return null;
  }

  function saveThemeCartSnapshot(cart) {
    if (!cart) return;
    state.lastThemeCart = cart;
    try {
      sessionStorage.setItem(THEME_CART_CACHE_KEY, JSON.stringify({ cart, savedAt: Date.now() }));
    } catch (error) {
      console.warn('[ScanCamera] Theme cart snapshot save failed', error);
    }
  }

  function normalizeCartProperties(properties) {
    if (!properties || typeof properties !== 'object') return null;
    const entries = Object.entries(properties).filter(([key, value]) => {
      if (!key) return false;
      return value != null;
    });
    if (!entries.length) return null;
    return Object.fromEntries(entries);
  }

  function getThemeCartPropertiesForVariant(variantId) {
    const cart = state.lastThemeCart || loadThemeCartSnapshot();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    if (!items.length) return null;
    const target = String(variantId).includes('/')
      ? String(variantId).split('/').pop()
      : String(variantId);
    const match = items.find((item) => String(item?.variant_id) === target);
    return normalizeCartProperties(match?.properties);
  }

  function serializeProperties(properties) {
    if (!properties) return '';
    const entries = Object.keys(properties).sort().map((key) => [key, properties[key]]);
    return JSON.stringify(entries);
  }


  function initThemeCartTap() {
    if (window.__scanCameraCartTap) return;
    window.__scanCameraCartTap = true;
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      try {
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;
        if (url && url.includes('/cart.js')) {
          const clone = response.clone();
          const data = await clone.json();
          if (data && typeof data === 'object') {
            saveThemeCartSnapshot(data);
          }
        }
      } catch (error) {
        // Ignore tap errors
      }
      return response;
    };

    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.__scanCameraUrl = url;
      return originalOpen.call(this, method, url, ...rest);
    };
    XMLHttpRequest.prototype.send = function (...args) {
      const handle = () => {
        try {
          const url = this.__scanCameraUrl || '';
          if (url && url.includes('/cart.js')) {
            const data = JSON.parse(this.responseText || '{}');
            if (data && typeof data === 'object') {
              saveThemeCartSnapshot(data);
            }
          }
        } catch (error) {
          // Ignore XHR tap errors
        }
      };
      this.addEventListener('load', handle);
      return originalSend.apply(this, args);
    };
  }

  function setCartResetDebug(reason, detail = null) {
    setBackendDebug({ cartReset: reason, detail });
  }

  function isScannerWorkspacePage() {
    try {
      if (typeof document !== 'undefined' && document.querySelector) {
        if (document.querySelector('[data-scan-workspace="true"]')) {
          return true;
        }
      }
      const path = typeof window.location.pathname === 'string' ? window.location.pathname : '';
      return path.indexOf('/pages/scanner') !== -1;
    } catch (e) {
      return false;
    }
  }

  function shouldUseInlineScannerEmbed() {
    if (!isScannerWorkspacePage()) return false;
    if (typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(max-width: 768.98px)').matches;
  }

  function getFriendlyCartErrorMessage(error) {
    const raw = String(error?.message || error || '');
    const lower = raw.toLowerCase();
    const d = overlay.dataset || {};
    const pick = (key, fallback) => {
      const v = d[key];
      return v && String(v).trim() ? String(v).trim() : fallback;
    };
    if (!raw) {
      return pick('errGeneric', 'Something went wrong. Please try again.');
    }
    if (raw === 'NOT_FOUND' || raw === 'SECURITY_CHALLENGE') {
      return null;
    }
    if (lower.includes('invalid json')) {
      return pick(
        'errInvalidResponse',
        "We couldn't confirm the cart update. Try again in a moment, or open the cart to verify."
      );
    }
    if (lower.includes('server cart endpoint missing')) {
      return pick(
        'errConfig',
        'Cart service is not configured. Please refresh the page or contact support.'
      );
    }
    if (lower.includes('sync_challenge') || lower.includes('sync challenge')) {
      return pick(
        'errNetwork',
        'The shop could not return a normal response (often a security check). Try again or refresh the page.'
      );
    }
    if (lower.includes('429') || lower.includes('403') || lower.includes('rate limit')) {
      return pick(
        'errRateLimit',
        'Too many requests. Wait a few seconds and try again.'
      );
    }
    if (lower.includes('server cart') && lower.includes('failed')) {
      return pick('errGeneric', "We couldn't update your cart. Please try again.");
    }
    if (raw.length > 160) {
      return pick('errGeneric', "We couldn't complete that action. Please try again.");
    }
    return raw;
  }

  function tryMountOverlayInline() {
    if (!shouldUseInlineScannerEmbed()) {
      return false;
    }
    const anchor = document.getElementById('scanner-camera-anchor');
    if (!anchor || !overlay.parentNode) {
      return false;
    }
    if (state.inlineEmbedPlace) {
      return true;
    }
    state.inlineEmbedPlace = {
      parent: overlay.parentNode,
      nextSibling: overlay.nextSibling
    };
    anchor.hidden = false;
    anchor.appendChild(overlay);
    overlay.classList.add('scan-camera-overlay--inline-embed');
    requestAnimationFrame(() => {
      try {
        anchor.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } catch (e) {}
    });
    return true;
  }

  function restoreOverlayInlineIfNeeded() {
    overlay.classList.remove('scan-camera-overlay--split-bottom');
    if (!state.inlineEmbedPlace) {
      return;
    }
    const place = state.inlineEmbedPlace;
    state.inlineEmbedPlace = null;
    overlay.classList.remove('scan-camera-overlay--inline-embed');
    try {
      if (place.nextSibling && place.nextSibling.parentNode === place.parent) {
        place.parent.insertBefore(overlay, place.nextSibling);
      } else {
        place.parent.appendChild(overlay);
      }
    } catch (e) {
      try {
        place.parent.appendChild(overlay);
      } catch (e2) {
        console.warn('[ScanCamera] Could not restore overlay parent', e2);
      }
    }
    const anchor = document.getElementById('scanner-camera-anchor');
    if (anchor) {
      anchor.hidden = true;
    }
  }

  function getScannerDebugSummary() {
    const cartId = getServerCartId();
    const cartTs = getServerCartTimestamp();
    const resetOnce = sessionStorage.getItem(CART_RESET_SESSION_KEY);
    return {
      cartId: cartId || null,
      cartIdAt: cartTs ? new Date(cartTs).toISOString() : null,
      cartIdAgeSeconds: cartTs ? Math.round((Date.now() - cartTs) / 1000) : null,
      isCartIdStale: isCartIdStale(),
      resetOnce: resetOnce ? '1' : '0'
    };
  }

  function openOverlay() {
    if (!state.enabled || state.open) return;
    state.open = true;
    state.securityHold = false;
    if (state.securityBanner) {
      state.securityBanner.remove();
      state.securityBanner = null;
    }
    scheduleSyncStoreHeaderHeight();
    const inlineMounted = tryMountOverlayInline();
    if (!inlineMounted && isScannerWorkspacePage() && window.matchMedia('(max-width: 768.98px)').matches) {
      overlay.classList.add('scan-camera-overlay--split-bottom');
    }
    if (isScannerWorkspacePage()) {
      overlay.classList.add('scan-camera-overlay--workspace');
      overlay.dataset.scanWorkspaceActive = 'true';
    }
    overlay.classList.add('scan-camera-overlay--open');
    overlay.setAttribute('aria-hidden', 'false');
    if (button) button.setAttribute('aria-expanded', 'true');
    setStatus('Ready to scan');
    setError('');
    state.lastDetectTime = 0;
    state.openedAt = Date.now();
    setStepNumber(14);
    primeAudio();
    ensureBackendDebugEl();
    setBackendDebug('Scanner opened');
    initThemeCartTap();
    loadThemeCartSnapshot();
    if (serverCartEnabled) {
      const resetOnce = sessionStorage.getItem(CART_RESET_SESSION_KEY);
      if (!resetOnce) {
        resetCartIdFromThemeCart().then((didReset) => {
          if (didReset) {
            sessionStorage.setItem(CART_RESET_SESSION_KEY, '1');
          }
          if (getServerCartId()) {
            setCartAdapter('storefront');
            initStorefrontCart();
          } else {
            setCartAdapter('theme');
          }
        });
      } else if (isCartIdStale()) {
        resetCartIdFromThemeCart().then(() => {
          if (getServerCartId()) {
            setCartAdapter('storefront');
            initStorefrontCart();
          } else {
            setCartAdapter('theme');
          }
        });
      } else if (getServerCartId()) {
        setCartAdapter('storefront');
        initStorefrontCart();
      } else {
        setCartAdapter('theme');
      }
    } else {
      setCartAdapter('theme');
    }
    startCamera();
    if (isScanDebugEnabled()) {
      updateScanDebugPanel();
      if (state.scanDebugInterval) {
        clearInterval(state.scanDebugInterval);
        state.scanDebugInterval = null;
      }
      state.scanDebugInterval = setInterval(updateScanDebugPanel, 400);
      setTimeout(updateScanDebugPanel, 50);
      setTimeout(updateScanDebugPanel, 600);
    }
  }

  function closeOverlay() {
    if (!state.open) return;
    if (state.processing) {
      state.closeRequested = true;
      showToast('Adding to cart…', 'success');
    }
    state.open = false;
    if (state.batchTimer) {
      clearTimeout(state.batchTimer);
      state.batchTimer = null;
    }
    if (state.cartRefreshTimer) {
      clearTimeout(state.cartRefreshTimer);
      state.cartRefreshTimer = null;
    }
    if (state.themeSyncTimer) {
      clearTimeout(state.themeSyncTimer);
      state.themeSyncTimer = null;
    }
    overlay.classList.remove('scan-camera-overlay--open');
    overlay.classList.remove('scan-camera-overlay--workspace');
    delete overlay.dataset.scanWorkspaceActive;
    if (state.scanDebugInterval) {
      clearInterval(state.scanDebugInterval);
      state.scanDebugInterval = null;
    }
    if (!isScanDebugEnabled()) {
      removeScanDebugPanel();
    } else {
      updateScanDebugPanel();
    }
    overlay.setAttribute('aria-hidden', 'true');
    if (button) button.setAttribute('aria-expanded', 'false');
    restoreOverlayInlineIfNeeded();
    stopCamera();
    setCartAdapter('theme');
    flushThemeSync();
    refreshCartUi();
  }

  function persistResumeState() {
    try {
      sessionStorage.setItem(RESUME_STORAGE_KEY, '1');
      sessionStorage.setItem(RESUME_RETURN_KEY, window.location.href);
      sessionStorage.setItem(RESUME_QUEUE_KEY, JSON.stringify(state.queue || []));
      sessionStorage.setItem(RESUME_PENDING_KEY, JSON.stringify(state.pendingAdds || []));
    } catch (error) {
      console.warn('[ScanCamera] Resume state persist failed', error);
    }
  }

  function restoreResumeState() {
    try {
      const resumeFlag = sessionStorage.getItem(RESUME_STORAGE_KEY);
      if (!resumeFlag) return;

      const returnUrl = sessionStorage.getItem(RESUME_RETURN_KEY);
      const params = new URLSearchParams(window.location.search);
      const isCartReturn = window.location.pathname.startsWith('/cart') && params.get('scan_return') === '1';
      if (returnUrl && window.location.pathname.startsWith('/cart') && !isCartReturn) {
        return;
      }

      sessionStorage.removeItem(RESUME_STORAGE_KEY);
      const queueRaw = sessionStorage.getItem(RESUME_QUEUE_KEY);
      const pendingRaw = sessionStorage.getItem(RESUME_PENDING_KEY);

      if (queueRaw) {
        try {
          state.queue = JSON.parse(queueRaw) || [];
        } catch (error) {
          // ignore malformed
        }
        sessionStorage.removeItem(RESUME_QUEUE_KEY);
      }
      if (pendingRaw) {
        try {
          state.pendingAdds = JSON.parse(pendingRaw) || [];
        } catch (error) {
          // ignore malformed
        }
        sessionStorage.removeItem(RESUME_PENDING_KEY);
      }

      sessionStorage.removeItem(RESUME_RETURN_KEY);
      if (!state.open) {
        setTimeout(() => {
          openOverlay();
        }, 200);
      } else {
        processQueue();
        flushCartBatch();
      }
    } catch (error) {
      console.warn('[ScanCamera] Resume state restore failed', error);
    }
  }

  async function startCamera() {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('Camera not available in this browser.', true);
        return;
      }

      state.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const constraints = {
        audio: false,
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: state.isIOS ? 1920 : 1280 },
          height: { ideal: state.isIOS ? 1080 : 720 }
        }
      };

      state.stream = await navigator.mediaDevices.getUserMedia(constraints);
      video.srcObject = state.stream;
      await video.play();
      await ensureDecoder();
      startDecodeLoop();
    } catch (error) {
      console.error('[ScanCamera] Camera start failed', error);
      setError('Permission denied or camera unavailable.', true);
    }
  }

  function stopCamera() {
    stopDecodeLoop();
    if (state.zxingReader && typeof state.zxingReader.reset === 'function') {
      state.zxingReader.reset();
    }
    if (state.stream) {
      state.stream.getTracks().forEach((track) => track.stop());
      state.stream = null;
    }
  }

  function getPolyfillGlobal() {
    if (window.barcodeDetectorPolyfill) return window.barcodeDetectorPolyfill;
    try {
      if (typeof barcodeDetectorPolyfill !== 'undefined') return barcodeDetectorPolyfill;
    } catch (error) {
      // ignore
    }
    return null;
  }

  function getZbarGlobal() {
    if (window.zbarWasm) return window.zbarWasm;
    try {
      if (typeof zbarWasm !== 'undefined') return zbarWasm;
    } catch (error) {
      // ignore
    }
    return null;
  }

  async function ensureDecoder() {
    if (state.zxingReader || state.detector) return;

    if (state.isIOS) {
      if (barcodePolyfillUrl) {
        try {
          if (!getZbarGlobal() && zbarWasmUrl) {
            await loadScript(zbarWasmUrl);
          }
          await loadScript(barcodePolyfillUrl);
          const zbarGlobal = getZbarGlobal();
          if (zbarGlobal && !window.zbarWasm) {
            window.zbarWasm = zbarGlobal;
          }
          const polyfillGlobal = getPolyfillGlobal();
          if (polyfillGlobal?.BarcodeDetectorPolyfill) {
            window.BarcodeDetector = polyfillGlobal.BarcodeDetectorPolyfill;
          }
        } catch (error) {
          console.warn('[ScanCamera] BarcodeDetector polyfill load failed', error);
        }
      }

      const polyfillGlobal = getPolyfillGlobal();
      const PolyfillDetector = polyfillGlobal?.BarcodeDetectorPolyfill;
      const DetectorClass = PolyfillDetector || window.BarcodeDetector;
      if (DetectorClass) {
        try {
          state.detector = new DetectorClass({
            formats: DETECTOR_FORMATS
          });
          return;
        } catch (error) {
          console.warn('[ScanCamera] BarcodeDetector init failed on iOS', error);
        }
      }

      if (!window.ZXing && zxingUrl) {
        try {
          await loadScript(zxingUrl);
        } catch (error) {
          console.warn('[ScanCamera] ZXing load failed on iOS', error);
        }
      }

      if (window.ZXing && window.ZXing.BrowserMultiFormatReader) {
        state.zxingReader = createZxingReader();
        return;
      }

      setError('Barcode decoder not available. Please contact support.', true);
      return;
    }

    if ('BarcodeDetector' in window) {
      try {
        state.detector = new BarcodeDetector({
          formats: DETECTOR_FORMATS
        });
      } catch (error) {
        console.warn('[ScanCamera] BarcodeDetector init failed', error);
      }
    }

    if (!state.detector) {
      setError('Barcode decoder not available. Please contact support.', true);
    }
  }

  function createZxingReader() {
    try {
      const hints = new Map();
      const formats = DETECTOR_FORMAT_KEYS.map((key) => {
        if (key === 'upc_a') return window.ZXing.BarcodeFormat.UPC_A;
        if (key === 'ean_13') return window.ZXing.BarcodeFormat.EAN_13;
        if (key === 'ean_8') return window.ZXing.BarcodeFormat.EAN_8;
        if (key === 'code_128') return window.ZXing.BarcodeFormat.CODE_128;
        if (key === 'itf_14') return window.ZXing.BarcodeFormat.ITF;
        return null;
      }).filter(Boolean);
      hints.set(window.ZXing.DecodeHintType.POSSIBLE_FORMATS, formats);
      hints.set(window.ZXing.DecodeHintType.TRY_HARDER, true);
      return new window.ZXing.BrowserMultiFormatReader(hints);
    } catch (error) {
      console.warn('[ScanCamera] ZXing hints init failed', error);
      return new window.ZXing.BrowserMultiFormatReader();
    }
  }

  function startZxingCanvasLoop() {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const loop = async (ts) => {
      if (!state.open || !state.zxingReader) return;
      if (ts - state.lastFrameTime < FRAME_INTERVAL_MS || state.isDecodingFrame) {
        state.rafId = requestAnimationFrame(loop);
        return;
      }
      state.lastFrameTime = ts;
      if (video.readyState >= 2) {
        const roi = getRoiForScan();
        drawRoiToCanvas(video, canvas, ctx, roi);
        state.isDecodingFrame = true;
        try {
          const result = await state.zxingReader.decodeFromCanvas(canvas);
          if (result) {
            const raw = result.text || result.getText?.();
            const format = result.getBarcodeFormat ? result.getBarcodeFormat() : '';
            handleDetected(raw, format);
          }
        } catch (error) {
          // ignore decode errors
        } finally {
          state.isDecodingFrame = false;
        }
      }
      state.rafId = requestAnimationFrame(loop);
    };
    state.rafId = requestAnimationFrame(loop);
  }

  function startZbarCanvasLoop() {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const loop = async (ts) => {
      const zbarGlobal = getZbarGlobal();
      if (!state.open || !zbarGlobal || !zbarGlobal.scanImageData) return;
      if (ts - state.lastFrameTime < IOS_FRAME_INTERVAL_MS || state.isDecodingFrame) {
        state.rafId = requestAnimationFrame(loop);
        return;
      }
      state.lastFrameTime = ts;
      if (video.readyState >= 2) {
        const roi = getRoiForScan();
        drawRoiToCanvas(video, canvas, ctx, roi);
        state.isDecodingFrame = true;
        try {
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const symbols = await zbarGlobal.scanImageData(imageData);
          if (symbols && symbols.length > 0) {
            const best = pickBestZbarSymbol(symbols, canvas.width, canvas.height);
            if (best) handleDetected(best.code, best.format);
          }
        } catch (error) {
          // ignore decode errors
        } finally {
          state.isDecodingFrame = false;
        }
      }
      state.rafId = requestAnimationFrame(loop);
    };
    state.rafId = requestAnimationFrame(loop);
  }

  function startDecodeLoop() {
    stopDecodeLoop();
    state.decodePipeline = '';
    state.decodeDrawPath = '';
    const zbarGlobal = getZbarGlobal();
    if (state.isIOS && zbarGlobal && zbarGlobal.scanImageData) {
      state.decodePipeline = 'zbar-canvas-loop';
      startZbarCanvasLoop();
      return;
    }

    if (state.zxingReader && state.isIOS) {
      try {
        state.decodePipeline = 'zxing-decodeFromVideoDevice-ios';
        state.zxingReader.decodeFromVideoDevice(null, video, (result) => {
          if (result) {
            const raw = result.text || result.getText?.();
            const format = result.getBarcodeFormat ? result.getBarcodeFormat() : '';
            handleDetected(raw, format);
          }
        });
      } catch (error) {
        console.warn('[ScanCamera] iOS ZXing video decode failed', error);
        state.decodePipeline = 'zxing-canvas-loop (fallback after ios video fail)';
        startZxingCanvasLoop();
      }
      return;
    }

    if (state.zxingReader) {
      try {
        state.decodePipeline = 'zxing-decodeFromVideoDevice-fullframe (NOT roi canvas)';
        state.zxingReader.decodeFromVideoDevice(null, video, (result, error) => {
          if (result) {
            const raw = result.text || result.getText?.();
            const format = result.getBarcodeFormat ? result.getBarcodeFormat() : '';
            handleDetected(raw, format);
          }
        });
      } catch (error) {
        console.warn('[ScanCamera] ZXing decode failed', error);
      }
      return;
    }

    if (!state.detector) {
      state.decodePipeline = 'none (no detector, no zxing)';
      return;
    }

    state.decodePipeline = 'barcode-detector-raf (drawRoiToCanvas each frame)';
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    const loop = (ts) => {
      if (!state.open || !state.detector) return;
      if (ts - state.lastFrameTime < FRAME_INTERVAL_MS) {
        state.rafId = requestAnimationFrame(loop);
        return;
      }
      state.lastFrameTime = ts;

      if (video.readyState >= 2) {
        const roi = getRoiForScan();
        drawRoiToCanvas(video, canvas, ctx, roi);

        if (isScanDebugEnabled()) {
          state.decodeDebugFrame += 1;
          if (state.decodeDebugFrame % 10 === 0) {
            try {
              state.lastDecodeStripImg = canvas.toDataURL('image/jpeg', 0.55);
            } catch (e) {
              state.lastDecodeStripImg = '';
            }
            updateScanDebugPanel();
          }
        }

        state.detector.detect(canvas).then((barcodes) => {
          if (barcodes && barcodes.length > 0) {
            const best = pickBestBarcode(barcodes, canvas.width, canvas.height);
            if (best) handleDetected(best.code, best.format);
          }
        }).catch(() => {});
      }

      state.rafId = requestAnimationFrame(loop);
    };

    state.rafId = requestAnimationFrame(loop);
  }

  function stopDecodeLoop() {
    if (state.rafId) {
      cancelAnimationFrame(state.rafId);
      state.rafId = null;
    }
  }

  function handleDetected(code, format = '') {
    const normalized = normalizeCode(code);
    const digits = sanitizeBarcode(normalized);
    const formatKey = normalizeFormat(format, digits);
    if (!isValidBarcode(normalized, digits, formatKey)) return;
    const now = Date.now();
    if (state.decodingLocked || now < state.cooldownUntil) return;

    state.lastDetectTime = now;
    state.decodingLocked = true;
    state.cooldownUntil = now + COOLDOWN_MS;
    triggerSuccessFeedback();
    enqueueScan(buildLookupCodes(normalized, digits, formatKey));
  }

  function enqueueScan(entry) {
    const codes = Array.isArray(entry) ? entry : [entry];
    if (typeof window.__isalbiScannerConsumeScan === 'function') {
      try {
        if (window.__isalbiScannerConsumeScan(codes)) {
          state.decodingLocked = false;
          return;
        }
      } catch (err) {
        console.warn('[ScanCamera] __isalbiScannerConsumeScan', err);
      }
    }
    state.queue.push(entry);
    processQueue();
  }

  function markSession(scanId, patch) {
    if (sessionStore && scanId) {
      sessionStore.update(scanId, patch);
    }
  }

  async function processQueue() {
    if (state.processing || state.queue.length === 0 || state.securityHold) {
      return;
    }

    state.processing = true;

    while (state.queue.length > 0) {
      const entry = state.queue.shift();
      const codes = Array.isArray(entry) ? entry : [entry];
      let scanId = null;
      if (sessionStore) {
        scanId = sessionStore.add({
          barcode: String(codes[0] || ''),
          status: SV.RESOLVING
        });
      }
      try {
        setStatus('Found code — adding...');
        setError('');
        const resolved = await resolveBarcode(codes);
        if (!resolved || !resolved.variantId) {
          throw new Error('NOT_FOUND');
        }
        const effectiveMoq = getEffectiveMoq(resolved.moq || 1);
        const quantity = normalizeQuantityByMoq(resolved.moq || 1, effectiveMoq);
        const title = resolved.title || resolved.productTitle || resolved.name || '';
        markSession(scanId, {
          status: SV.RESOLVED,
          title,
          variantId: resolved.variantId,
          quantity
        });
        enqueueBatchItem({
          variantId: resolved.variantId,
          quantity,
          moq: effectiveMoq,
          name: title,
          scanId
        });
        setStatus('Ready to scan');
      } catch (error) {
        if (String(error?.message || '') === 'SECURITY_CHALLENGE') {
          state.queue.unshift(entry);
          setStatus('Security check triggered');
          showSecurityBanner();
          break;
        }
        if (String(error?.message || '').includes('429') || String(error?.message || '').includes('403')) {
          state.cartBackoffMs = Math.min(8000, Math.max(state.cartBackoffMs * 2, CART_BACKOFF_BASE_MS));
        }
        const message = error.message || 'Add failed';
        console.warn('[ScanCamera] Resolve/add failed', error);
        let lineError = message;
        if (message === 'NOT_FOUND') {
          lineError = 'Product not found';
        } else {
          const friendly = getFriendlyCartErrorMessage(error);
          if (friendly) {
            lineError = friendly;
          }
        }
        markSession(scanId, {
          status: SV.FAILED,
          error: lineError
        });
        if (message === 'NOT_FOUND') {
          showToast('Product not found', 'error');
          setError('Product not found. Try search instead.', true);
        } else if (String(message).toLowerCase().includes('sold out')) {
          showToast('Product sold out', 'error');
          setError('Product sold out.', true);
        } else {
          const toastMsg = getFriendlyCartErrorMessage(error) || 'Add failed — retry';
          showToast(toastMsg, 'error');
          setError(toastMsg, false);
        }
        setStatus('Ready to scan');
      }
    }

    state.processing = false;
    state.decodingLocked = false;
    state.closeRequested = false;
  }

  function enqueueBatchItem(item) {
    state.pendingAdds.push(item);
    markSession(item.scanId, { status: SV.QUEUED });
    if (state.pendingAdds.length >= CART_BATCH_MAX) {
      flushCartBatch();
      return;
    }
    if (state.batchInFlight) return;
    if (state.batchTimer) return;
    const batchWindow = serverCartEnabled ? CART_BATCH_WINDOW_SERVER_MS : THEME_CART_BATCH_WINDOW_MS;
    state.batchTimer = setTimeout(() => {
      state.batchTimer = null;
      flushCartBatch();
    }, batchWindow);
  }

  async function flushCartBatch() {
    if (state.batchInFlight || state.pendingAdds.length === 0) return;
    state.batchInFlight = true;
    const batch = state.pendingAdds.splice(0, CART_BATCH_MAX);
    batch.forEach((line) => {
      markSession(line.scanId, { status: SV.SENDING });
    });
    try {
      await waitForCartSlot({ themeSync: !serverCartEnabled });
      const result = await addToCartBatch(batch);
      state.cartBackoffMs = 0;
      if (!result?.serverCart) {
        scheduleCartRefresh();
      }
      batch.forEach((line) => {
        markSession(line.scanId, { status: SV.COMMITTED });
      });
      showToast(`Added ${batch.length} item${batch.length === 1 ? '' : 's'} ✓`, 'success');
    } catch (error) {
      if (String(error?.message || '') === 'SECURITY_CHALLENGE') {
        state.pendingAdds = batch.concat(state.pendingAdds);
        showSecurityBanner();
        return;
      }
      console.warn('[ScanCamera] Batch add failed', error);
      const batchMsg = getFriendlyCartErrorMessage(error) || 'Batch add failed — retrying items';
      showToast(batchMsg, 'error');
      setError(batchMsg, false);
      if (String(error?.message || '').includes('429') || String(error?.message || '').includes('403')) {
        state.cartBackoffMs = Math.min(8000, Math.max(state.cartBackoffMs * 2, CART_BACKOFF_BASE_MS));
      }
      await handleBatchFailure(batch);
    } finally {
      state.batchInFlight = false;
      if (state.pendingAdds.length > 0) {
        flushCartBatch();
      }
    }
  }

  function scheduleCartRefresh() {
    state.needsCartRefresh = true;
    if (state.cartRefreshTimer) {
      clearTimeout(state.cartRefreshTimer);
    }
    state.cartRefreshTimer = setTimeout(() => {
      if (state.batchInFlight || state.processing) {
        scheduleCartRefresh();
        return;
      }
      if (!state.needsCartRefresh) return;
      state.needsCartRefresh = false;
      state.cartRefreshTimer = null;
      refreshCartUi();
    }, 1500);
  }

  function refreshCartUi() {
    if (state.cartAdapter === 'storefront') return;
    try {
      if (window.ProductForm && typeof window.ProductForm.updateCartIcon === 'function') {
        window.ProductForm.updateCartIcon();
      }
    } catch (error) {
      console.warn('[ScanCamera] Cart refresh failed', error);
    }
  }

  async function handleBatchFailure(batch) {
    const failedNames = [];
    let anyAdded = false;
    let anyServerAdds = false;
    for (const item of batch) {
      try {
        await waitForCartSlot({ themeSync: !serverCartEnabled });
        const result = await addToCart(item.variantId, item.quantity, item.moq || 1);
        if (result?.serverCart) {
          anyServerAdds = true;
        }
        anyAdded = true;
        markSession(item.scanId, { status: SV.COMMITTED });
      } catch (error) {
        if (String(error?.message || '') === 'SECURITY_CHALLENGE') {
          state.pendingAdds = batch.concat(state.pendingAdds);
          showSecurityBanner();
          return;
        }
        failedNames.push(item.name || `Item ${item.variantId}`);
        const oneMsg = getFriendlyCartErrorMessage(error) || error?.message || 'Add failed';
        markSession(item.scanId, {
          status: SV.FAILED,
          error: oneMsg
        });
      }
    }
    if (anyAdded && !anyServerAdds) {
      scheduleCartRefresh();
    }
    if (failedNames.length > 0) {
      showToast('Some items failed', 'error');
      setError(`Failed to add: ${failedNames.join(', ')}`, true);
    }
  }

  function waitForCartSlot(options = {}) {
    const now = Date.now();
    const isThemeSync = Boolean(options.themeSync);
    const baseDelay = Math.max(
      0,
      Math.max(
        CART_MIN_DELAY_MS - (now - state.lastCartAddAt),
        (isThemeSync ? THEME_CART_TRAILING_WAIT_MS : CART_TRAILING_WAIT_MS) - (now - state.lastCartAddAt)
      )
    );
    const backoffDelay = state.cartBackoffMs || 0;
    const jitter = Math.floor(Math.random() * (isThemeSync ? THEME_CART_JITTER_MS : CART_JITTER_MS));
    const delay = baseDelay + backoffDelay + jitter;
    if (delay <= 0) return Promise.resolve();
    return new Promise((resolve) => setTimeout(resolve, delay));
  }

  function normalizeCode(code) {
    return String(code || '').trim().toUpperCase().replace(/\s+/g, '');
  }

  function getEffectiveMoq(rawMoq) {
    const moqUtils = window.__isalbiMoqUtils;
    const productMoq = parseInt(rawMoq, 10);
    const overrideMoq = moqUtils && typeof moqUtils.getGlobalOverrideMoq === 'function'
      ? moqUtils.getGlobalOverrideMoq()
      : null;
    if (moqUtils && typeof moqUtils.resolveEffectiveMoq === 'function') {
      return moqUtils.resolveEffectiveMoq({ overrideMoq, productMoq, fallback: 1 });
    }
    if (Number.isFinite(overrideMoq) && overrideMoq > 0) return overrideMoq;
    return Number.isFinite(productMoq) && productMoq > 0 ? productMoq : 1;
  }

  function normalizeQuantityByMoq(quantity, rawMoq) {
    const moq = getEffectiveMoq(rawMoq);
    const moqUtils = window.__isalbiMoqUtils;
    if (moqUtils && typeof moqUtils.normalizeQuantityToMoq === 'function') {
      return moqUtils.normalizeQuantityToMoq(quantity, moq).quantity;
    }
    const qty = parseInt(quantity, 10);
    const safeQty = Number.isFinite(qty) ? qty : moq;
    return Math.max(moq, Math.ceil(safeQty / moq) * moq);
  }

  function sanitizeBarcode(code) {
    return String(code || '').replace(/\D+/g, '');
  }

  function isValidBarcode(raw, digits, formatKey) {
    if (formatKey === 'code_128') {
      return Boolean(raw);
    }
    return isValidGtinBarcode(digits, formatKey);
  }

  function isValidGtinBarcode(digits, formatKey) {
    if (!digits) return false;
    const allowedLengths = state.isIOS ? IOS_GTIN_LENGTHS : DEFAULT_GTIN_LENGTHS;
    if (!allowedLengths.includes(digits.length)) return false;
    if (formatKey === 'upc_a' && digits.length !== 12) return false;
    if (formatKey === 'ean_13' && digits.length !== 13) return false;
    if (formatKey === 'ean_8' && digits.length !== 8) return false;
    if (formatKey === 'itf_14' && digits.length !== 14) return false;
    if (validateGtin(digits)) return true;
    // Allow leading-zero EAN-13 values that validate as UPC-12.
    if (digits.length === 13 && digits.startsWith('0')) {
      return validateGtin(digits.slice(1));
    }
    return false;
  }

  function normalizeFormat(format, digits) {
    const raw = String(format || '').toLowerCase();
    if (raw.includes('upc_a') || raw.includes('upca') || raw === 'upc-a') return 'upc_a';
    if (raw.includes('ean_13') || raw.includes('ean13') || raw === 'ean-13') return 'ean_13';
    if (raw.includes('ean_8') || raw.includes('ean8') || raw === 'ean-8') return 'ean_8';
    if (raw.includes('itf') || raw.includes('itf_14') || raw === 'itf-14') return 'itf_14';
    if (raw.includes('code_128') || raw.includes('code128') || raw === 'code-128') return 'code_128';
    if (digits && digits.length === 12) return 'upc_a';
    if (digits && digits.length === 8) return 'ean_8';
    if (digits && digits.length === 13) return 'ean_13';
    if (digits && digits.length === 14) return 'itf_14';
    return '';
  }

  function buildLookupCodes(raw, digits, formatKey) {
    const codes = new Set();
    if (formatKey === 'code_128') {
      if (raw) codes.add(raw);
      return Array.from(codes);
    }
    if (!isValidGtinBarcode(digits, formatKey)) return [];

    // Normalize to canonical GTIN-14 + useful derived forms.
    if (formatKey === 'upc_a' && digits.length === 12) {
      codes.add(digits); // GTIN-12
      codes.add(`0${digits}`); // EAN-13
      codes.add(`00${digits}`); // GTIN-14
      return Array.from(codes);
    }

    if (formatKey === 'ean_13' && digits.length === 13) {
      codes.add(digits); // GTIN-13
      if (digits.startsWith('0')) {
        codes.add(digits.slice(1)); // UPC-12
      }
      codes.add(`0${digits}`); // GTIN-14
      return Array.from(codes);
    }

    if (formatKey === 'ean_8' && digits.length === 8) {
      codes.add(digits); // GTIN-8
      codes.add(`000000${digits}`); // GTIN-14
      return Array.from(codes);
    }

    if (formatKey === 'itf_14' && digits.length === 14) {
      codes.add(digits); // GTIN-14
      if (digits.startsWith('0')) {
        codes.add(digits.slice(1)); // GTIN-13
      }
      if (digits.startsWith('00')) {
        codes.add(digits.slice(2)); // GTIN-12
      }
      return Array.from(codes);
    }

    // Unknown format: fallback to deterministic GTIN length handling.
    if (digits.length === 12) return buildLookupCodes(raw, digits, 'upc_a');
    if (digits.length === 13) return buildLookupCodes(raw, digits, 'ean_13');
    if (digits.length === 8) return buildLookupCodes(raw, digits, 'ean_8');
    if (digits.length === 14) return buildLookupCodes(raw, digits, 'itf_14');
    return [];
  }

  function validateGtin(digits) {
    const len = digits.length;
    let sum = 0;
    for (let i = 0; i < len - 1; i += 1) {
      const num = parseInt(digits[len - 2 - i], 10);
      sum += (i % 2 === 0) ? num * 3 : num;
    }
    const check = (10 - (sum % 10)) % 10;
    return check === parseInt(digits[len - 1], 10);
  }

  function pickBestBarcode(barcodes, width, height) {
    if (!Array.isArray(barcodes)) return null;
    const cx = width / 2;
    const cy = height / 2;
    const candidates = barcodes.map((barcode) => {
      const raw = barcode.rawValue || '';
      const normalized = normalizeCode(raw);
      const digits = sanitizeBarcode(normalized);
      const formatKey = normalizeFormat(barcode.format, digits);
      if (!isValidBarcode(normalized, digits, formatKey)) return null;
      if (!FORMAT_PRIORITY.includes(formatKey)) return null;
      const bbox = barcode.boundingBox || { x: 0, y: 0, width: 0, height: 0 };
      const bx = (bbox.x || 0) + (bbox.width || 0) / 2;
      const by = (bbox.y || 0) + (bbox.height || 0) / 2;
      const distance = Math.hypot(bx - cx, by - cy);
      const codeValue = formatKey === 'code_128' ? normalized : digits;
      return { code: codeValue, format: formatKey, distance };
    }).filter(Boolean);

    if (!candidates.length) return null;
    const bestPriority = Math.min(...candidates.map((item) => FORMAT_PRIORITY.indexOf(item.format)));
    const preferred = candidates.filter((item) => FORMAT_PRIORITY.indexOf(item.format) === bestPriority);
    preferred.sort((a, b) => a.distance - b.distance);
    return preferred[0];
  }

  function pickBestZbarSymbol(symbols, width, height) {
    if (!Array.isArray(symbols)) return null;
    const cx = width / 2;
    const cy = height / 2;
    const candidates = symbols.map((symbol) => {
      const raw = typeof symbol.decode === 'function' ? symbol.decode() : symbol.rawData;
      const normalized = normalizeCode(raw);
      const digits = sanitizeBarcode(normalized);
      const formatKey = normalizeFormat(symbol.typeName || symbol.type, digits);
      if (!isValidBarcode(normalized, digits, formatKey)) return null;
      if (!FORMAT_PRIORITY.includes(formatKey)) return null;
      const points = Array.isArray(symbol.points) ? symbol.points : [];
      const codeValue = formatKey === 'code_128' ? normalized : digits;
      if (!points.length) return { code: codeValue, format: formatKey, distance: 0 };
      const avg = points.reduce((acc, point) => {
        acc.x += point.x;
        acc.y += point.y;
        return acc;
      }, { x: 0, y: 0 });
      const bx = avg.x / points.length;
      const by = avg.y / points.length;
      const distance = Math.hypot(bx - cx, by - cy);
      return { code: codeValue, format: formatKey, distance };
    }).filter(Boolean);

    if (!candidates.length) return null;
    const bestPriority = Math.min(...candidates.map((item) => FORMAT_PRIORITY.indexOf(item.format)));
    const preferred = candidates.filter((item) => FORMAT_PRIORITY.indexOf(item.format) === bestPriority);
    preferred.sort((a, b) => a.distance - b.distance);
    return preferred[0];
  }

  /**
   * Layout box for object-fit: cover — must match the <video> replaced element's laid-out size.
   * Use getBoundingClientRect() (subpixel) so cover crop matches the compositor; clientWidth/Height
   * are integers and can skew scale vs CSS, producing a different vertical slice than on-screen preview.
   */
  function getVideoLayoutBoxCssPx(videoEl) {
    const r = videoEl.getBoundingClientRect();
    let cw = r.width;
    let ch = r.height;
    if (cw < 1 || ch < 1) {
      cw = videoEl.clientWidth;
      ch = videoEl.clientHeight;
    }
    if (cw < 1 || ch < 1) {
      const vp = videoEl.closest && videoEl.closest('.scan-camera-viewport');
      if (vp) {
        const vr = vp.getBoundingClientRect();
        cw = vr.width;
        ch = vr.height;
      }
    }
    return { cw: Math.max(1, cw), ch: Math.max(1, ch) };
  }

  /**
   * object-fit: cover — visible portion of the *source* frame in native buffer coordinates (drawImage space).
   * Must match how the browser samples the video texture into the element box (same formula as CSS cover).
   */
  function getVisibleSourceRect(videoEl) {
    const vw = videoEl.videoWidth;
    const vh = videoEl.videoHeight;
    if (!vw || !vh) {
      return { sx: 0, sy: 0, sw: Math.max(1, vw || 1), sh: Math.max(1, vh || 1) };
    }
    const { cw: cwF, ch: chF } = getVideoLayoutBoxCssPx(videoEl);
    const scale = Math.max(cwF / vw, chF / vh);
    const scaledW = vw * scale;
    const scaledH = vh * scale;
    const ox = (scaledW - cwF) / 2;
    const oy = (scaledH - chF) / 2;
    return {
      sx: ox / scale,
      sy: oy / scale,
      sw: cwF / scale,
      sh: chF / scale
    };
  }

  function intersectRects(a, b) {
    const x1 = Math.max(a.sx, b.sx);
    const y1 = Math.max(a.sy, b.sy);
    const x2 = Math.min(a.sx + a.sw, b.sx + b.sw);
    const y2 = Math.min(a.sy + a.sh, b.sy + b.sh);
    if (x2 <= x1 || y2 <= y1) return null;
    return { sx: x1, sy: y1, sw: x2 - x1, sh: y2 - y1 };
  }

  function fullFrameRoiToPixels(roiNorm, vw, vh) {
    return {
      sx: vw * roiNorm.x,
      sy: vh * roiNorm.y,
      sw: vw * roiNorm.w,
      sh: vh * roiNorm.h
    };
  }

  function clampCropToVideoBounds(crop, vw, vh) {
    const sx = Math.max(0, Math.min(crop.sx, vw - 1));
    const sy = Math.max(0, Math.min(crop.sy, vh - 1));
    const sw = Math.max(1, Math.min(crop.sw, vw - sx));
    const sh = Math.max(1, Math.min(crop.sh, vh - sy));
    return { sx, sy, sw, sh };
  }

  /**
   * Non–workspace: ROI is full-frame normalized; intersect with visible (object-fit cover) in buffer space.
   * /pages/scanner workspace uses naive full-buffer crop in drawRoiToCanvas instead (see comment there).
   */
  function getDecoderCropPixels(videoEl, roiNorm) {
    const vw = videoEl.videoWidth;
    const vh = videoEl.videoHeight;
    const vis = getVisibleSourceRect(videoEl);
    const visRect = { sx: vis.sx, sy: vis.sy, sw: vis.sw, sh: vis.sh };

    const proposed = fullFrameRoiToPixels(roiNorm, vw, vh);
    const hit = intersectRects(proposed, visRect);
    if (!hit || hit.sw < 8 || hit.sh < 8) {
      return clampCropToVideoBounds(visRect, vw, vh);
    }
    return clampCropToVideoBounds(hit, vw, vh);
  }

  /** Chrome/Android: MediaTrackSettings.rotation (0, 90, 180, 270). iOS often omits it. */
  function getVideoRotationDegreesFromTrack(videoEl) {
    try {
      const stream = videoEl.srcObject;
      if (!stream || typeof stream.getVideoTracks !== 'function') return 0;
      const tracks = stream.getVideoTracks();
      if (!tracks || !tracks.length) return 0;
      const track = tracks[0];
      if (track && typeof track.getSettings === 'function') {
        const s = track.getSettings();
        if (s && typeof s.rotation === 'number' && Number.isFinite(s.rotation)) {
          let r = Math.round(s.rotation) % 360;
          if (r < 0) r += 360;
          return r;
        }
      }
    } catch (e) {
      /* ignore */
    }
    return 0;
  }

  /** URL ?scan_debug=1 or sessionStorage scan_debug=1 — shows fixed debug strip (see updateScanDebugPanel). */
  function isScanDebugEnabled() {
    try {
      if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('scan_debug') === '1') {
        return true;
      }
      const q = typeof window.location.search === 'string' ? window.location.search : '';
      return /[?&]scan_debug=(1|true|yes)(?:&|$)/i.test(q);
    } catch (e) {
      return false;
    }
  }

  function removeScanDebugPanel() {
    const el = document.getElementById('scan-camera-debug-panel');
    if (el) el.remove();
  }

  function applyScanDebugPanelShellStyles(el) {
    el.style.cssText = [
      'position:fixed',
      'left:0',
      'right:0',
      'bottom:0',
      'z-index:2147483646',
      'max-height:min(52vh,520px)',
      'overflow-x:hidden',
      'overflow-y:auto',
      '-webkit-overflow-scrolling:touch',
      'overscroll-behavior:contain',
      'touch-action:pan-y',
      'pointer-events:auto',
      'padding:10px 12px',
      'padding-bottom:max(12px, env(safe-area-inset-bottom))',
      'background:rgba(0,0,0,0.94)',
      'color:#a3e635',
      'font:11px/1.4 ui-monospace,SFMono-Regular,monospace',
      'border-top:1px solid #3f3f46',
      'box-sizing:border-box'
    ].join(';');
  }

  function updateScanDebugPanel() {
    if (!isScanDebugEnabled()) {
      removeScanDebugPanel();
      return;
    }
    let el = document.getElementById('scan-camera-debug-panel');
    if (!el) {
      el = document.createElement('div');
      el.id = 'scan-camera-debug-panel';
      el.setAttribute('role', 'status');
      document.body.appendChild(el);
    }
    applyScanDebugPanelShellStyles(el);
    const path = typeof window.location.pathname === 'string' ? window.location.pathname : '';
    const hasDomFlag = !!(document.querySelector && document.querySelector('[data-scan-workspace="true"]'));
    const ws = isScannerWorkspacePage();
    const vw = video && video.videoWidth ? video.videoWidth : 0;
    const vh = video && video.videoHeight ? video.videoHeight : 0;
    const vcw = video && video.clientWidth ? video.clientWidth : 0;
    const vch = video && video.clientHeight ? video.clientHeight : 0;
    let vpDbg = '';
    try {
      if (video && video.closest) {
        const vp = video.closest('.scan-camera-viewport');
        if (vp) {
          const vpr = vp.getBoundingClientRect();
          const vdr = video.getBoundingClientRect();
          vpDbg =
            '  viewport vs video (css px): ' +
            vpr.width.toFixed(1) +
            '×' +
            vpr.height.toFixed(1) +
            ' vs ' +
            vdr.width.toFixed(1) +
            '×' +
            vdr.height.toFixed(1);
        }
      }
    } catch (e) {
      vpDbg = '';
    }
    let rot = 0;
    try {
      rot = video && vw ? getVideoRotationDegreesFromTrack(video) : -1;
    } catch (e) {
      rot = -1;
    }
    const oa = overlay && overlay.dataset && overlay.dataset.scanWorkspaceActive;
    let trackRot = 'n/a';
    try {
      if (video) trackRot = String(getVideoRotationDegreesFromTrack(video));
    } catch (e) {
      trackRot = 'err';
    }
    const lines = [
      'scan_camera debug ON — remove ?scan_debug=1 or sessionStorage.removeItem("scan_debug")',
      'pathname: ' + path,
      'isScannerWorkspacePage(): ' + ws,
      'DOM [data-scan-workspace]: ' + hasDomFlag,
      'decodePipeline: ' + (state.decodePipeline || '—'),
      'lastDrawPath: ' + (state.decodeDrawPath || '—'),
      'detector: ' + (state.detector ? 'yes' : 'no') + '  zxingReader: ' + (state.zxingReader ? 'yes' : 'no'),
      'video buffer: ' + vw + 'x' + vh + '  element client: ' + vcw + 'x' + vch + vpDbg,
      'track.rotation (getSettings): ' + trackRot + '  (debug rot) ' + rot,
      'overlay data-scan-workspace-active: ' + (oa ? 'yes' : 'no'),
      'overlay open: ' + (state.open ? 'yes' : 'no')
    ];
    el.replaceChildren();
    const textBlock = document.createElement('div');
    textBlock.style.whiteSpace = 'pre-wrap';
    textBlock.style.wordBreak = 'break-word';
    textBlock.textContent = lines.join('\n');
    el.appendChild(textBlock);
    if (state.lastDecodeStripImg) {
      const lab2 = document.createElement('div');
      lab2.textContent = 'Decoder input strip (BarcodeDetector sees this bitmap):';
      lab2.style.cssText = 'color:#e2e8f0;font-size:10px;margin-top:8px';
      el.appendChild(lab2);
      const imgS = document.createElement('img');
      imgS.src = state.lastDecodeStripImg;
      imgS.alt = '';
      imgS.style.cssText =
        'display:block;width:100%;max-width:100%;height:auto;max-height:min(22vh,200px);object-fit:contain;box-sizing:border-box;border:1px solid #22c55e';
      el.appendChild(imgS);
    }
  }

  /**
   * /pages/scanner workspace: same naive crop as early scan-camera (before object-fit alignment work).
   * ROI is interpreted as fractions of the raw video buffer (videoWidth × videoHeight). That does not
   * match the object-fit:cover reticle 1:1 in general — decoding may work while the box is slightly
   * wrong. Next step: map reticle (CSS box) → visible source rect, then crop.
   */
  function drawRoiToCanvas(videoEl, canvasEl, context, roi) {
    if (isScannerWorkspacePage()) {
      state.decodeDrawPath = 'workspace: naive buffer crop (full-frame ROI fractions)';
      const vw = videoEl.videoWidth;
      const vh = videoEl.videoHeight;
      if (!vw || !vh) return;
      const sx = Math.floor(vw * roi.x);
      const sy = Math.floor(vh * roi.y);
      const sw = Math.max(1, Math.floor(vw * roi.w));
      const sh = Math.max(1, Math.floor(vh * roi.h));
      canvasEl.width = sw;
      canvasEl.height = sh;
      context.drawImage(videoEl, sx, sy, sw, sh, 0, 0, sw, sh);
      return;
    }
    state.decodeDrawPath = 'non-workspace: buffer crop (getDecoderCropPixels)';
    const crop = getDecoderCropPixels(videoEl, roi);
    const sx = Math.floor(crop.sx);
    const sy = Math.floor(crop.sy);
    const sw = Math.max(1, Math.floor(crop.sw));
    const sh = Math.max(1, Math.floor(crop.sh));
    canvasEl.width = sw;
    canvasEl.height = sh;
    context.drawImage(videoEl, sx, sy, sw, sh, 0, 0, sw, sh);
  }


  function getIosActiveRoi() {
    const now = Date.now();
    const sinceOpen = now - (state.openedAt || now);
    const sinceDetect = now - (state.lastDetectTime || 0);
    const idleMs = state.lastDetectTime > 0 ? sinceDetect : sinceOpen;
    if (idleMs > IOS_FULL_ROI_AFTER_MS) {
      return FULL_ROI;
    }
    if ((state.lastDetectTime === 0 && sinceOpen > IOS_ROI_EXPAND_AFTER_MS) ||
        (state.lastDetectTime > 0 && sinceDetect > IOS_ROI_EXPAND_AFTER_MS)) {
      return EXPANDED_ROI;
    }
    return ROI;
  }

  function getIosActiveRoiWorkspace() {
    const now = Date.now();
    const sinceOpen = now - (state.openedAt || now);
    const sinceDetect = now - (state.lastDetectTime || 0);
    const idleMs = state.lastDetectTime > 0 ? sinceDetect : sinceOpen;
    if (idleMs > IOS_FULL_ROI_AFTER_MS) {
      return SCANNER_PAGE_EXPANDED_ROI;
    }
    if ((state.lastDetectTime === 0 && sinceOpen > IOS_ROI_EXPAND_AFTER_MS) ||
        (state.lastDetectTime > 0 && sinceDetect > IOS_ROI_EXPAND_AFTER_MS)) {
      return SCANNER_PAGE_EXPANDED_ROI;
    }
    return SCANNER_PAGE_ROI;
  }

  function getRoiForScan() {
    if (!isScannerWorkspacePage()) {
      return state.isIOS ? getIosActiveRoi() : ROI;
    }
    return state.isIOS ? getIosActiveRoiWorkspace() : SCANNER_PAGE_ROI;
  }

  function loadCache() {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const now = Date.now();
      Object.entries(parsed).forEach(([key, entry]) => {
        if (now - entry.timestamp < CACHE_TTL) {
          state.cache.set(key, entry.data);
        }
      });
    } catch (error) {
      console.warn('[ScanCamera] Cache load failed', error);
    }
  }

  function saveCache() {
    try {
      const entries = Array.from(state.cache.entries()).reduce((acc, [key, data]) => {
        acc[key] = { data, timestamp: Date.now() };
        return acc;
      }, {});
      localStorage.setItem(CACHE_KEY, JSON.stringify(entries));
    } catch (error) {
      console.warn('[ScanCamera] Cache save failed', error);
    }
  }

  async function resolveBarcode(codes) {
    const candidates = Array.isArray(codes) ? codes : [codes];
    for (const code of candidates) {
      const cached = state.cache.get(code);
      if (cached) return cached;
    }

    for (const code of candidates) {
      const mapResult = await resolveFromMap(code);
      if (mapResult) {
        state.cache.set(code, mapResult);
        saveCache();
        return mapResult;
      }
    }

    if (resolveUrl && shopDomain) {
      for (const code of candidates) {
        const payload = await resolveFromApi(code);
        if (payload) {
          state.cache.set(code, payload);
          saveCache();
          return payload;
        }
      }
    }

    throw new Error('NOT_FOUND');
  }

  async function resolveFromMap(code) {
    if (!barcodeIndexUrl) return null;
    await loadIndex();
    if (!state.index) return null;

    const prefixLength = state.index.prefixLength || 2;
    const prefix = code.slice(0, prefixLength);
    const shardFile = state.index.shards ? state.index.shards[prefix] : state.index[prefix];
    if (!shardFile) return null;

    const shardData = await loadShard(shardFile, prefix);
    if (!shardData) return null;

    const entry = shardData[code];
    if (!entry) return null;

    return {
      variantId: entry.variantId || entry.variant_id || entry.id,
      moq: entry.moq || entry.minimum_order_quantity || 1,
      title: entry.title || entry.product_title || entry.name || ''
    };
  }

  async function loadIndex() {
    if (state.index) return;
    try {
      const response = await fetch(barcodeIndexUrl, { cache: 'force-cache' });
      if (!response.ok) return;
      state.index = await response.json();
    } catch (error) {
      console.warn('[ScanCamera] Index load failed', error);
    }
  }

  async function loadShard(shardFile, prefix) {
    if (state.shardCache.has(prefix)) {
      return state.shardCache.get(prefix);
    }
    try {
      const url = shardFile.startsWith('http') ? shardFile : `/assets/${shardFile}`;
      const response = await fetch(url, { cache: 'force-cache' });
      if (!response.ok) return null;
      const data = await response.json();
      state.shardCache.set(prefix, data);
      return data;
    } catch (error) {
      console.warn('[ScanCamera] Shard load failed', error);
      return null;
    }
  }

  async function resolveFromApi(code) {
    try {
      const response = await fetch(`${resolveUrl}?shop=${encodeURIComponent(shopDomain)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code })
      });
      if (!response.ok) return null;
      const data = await response.json();
      if (!data) return null;
      const var_id = data.variantId || data.variant_id || data.id;
      if (!data.ok && !var_id) return null;
      if (data.productId == null) return null;
      if (!var_id) return null;
      return {
        variantId: var_id,
        moq: data.moq || data.metadata?.moq || 1,
        title: data.title || data.productTitle || data.product?.title || ''
      };
    } catch (error) {
      console.warn('[ScanCamera] API resolve failed', error);
      return null;
    }
  }

  function getServerCartUrl() {
    const explicitUrl = overlay.dataset.serverCartUrl;
    if (explicitUrl) return explicitUrl;
    if (!resolveUrl) return '';
    try {
      const url = new URL(resolveUrl, window.location.origin);
      return `${url.origin}/api/scanner/cart/add`;
    } catch (error) {
      return '';
    }
  }

  function getServerCartId() {
    try {
      return localStorage.getItem(SERVER_CART_STORAGE_KEY);
    } catch (error) {
      return null;
    }
  }

  function getServerCartTimestamp() {
    try {
      const raw = localStorage.getItem(SERVER_CART_CREATED_KEY);
      const parsed = raw ? Number(raw) : 0;
      return Number.isFinite(parsed) ? parsed : 0;
    } catch (error) {
      return 0;
    }
  }

  function setServerCartId(cartId) {
    try {
      if (!cartId) {
        localStorage.removeItem(SERVER_CART_STORAGE_KEY);
        localStorage.removeItem(SERVER_CART_CREATED_KEY);
        return;
      }
      const current = localStorage.getItem(SERVER_CART_STORAGE_KEY);
      if (current !== cartId) {
        localStorage.setItem(SERVER_CART_STORAGE_KEY, cartId);
        localStorage.setItem(SERVER_CART_CREATED_KEY, String(Date.now()));
        return;
      }
      if (!localStorage.getItem(SERVER_CART_CREATED_KEY)) {
        localStorage.setItem(SERVER_CART_CREATED_KEY, String(Date.now()));
      }
    } catch (error) {
      console.warn('[ScanCamera] CartId storage failed', error);
    }
  }

  function isCartIdStale() {
    const cartId = getServerCartId();
    if (!cartId) return false;
    const ts = getServerCartTimestamp();
    if (!ts) return true;
    return Date.now() - ts > CART_ID_TTL_MS;
  }

  async function fetchStorefrontCart(cartId) {
    const serverCartUrl = getServerCartUrl();
    if (!serverCartUrl || !cartId) return null;
    const requestUrl = shopDomain ? `${serverCartUrl}?shop=${encodeURIComponent(shopDomain)}` : serverCartUrl;
    try {
      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ cartId, lines: [] })
      });
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) return null;
      const data = await response.json();
      if (!response.ok || !data?.ok || !data?.cart) return null;
      return data.cart;
    } catch (error) {
      console.warn('[ScanCamera] Storefront cart fetch failed', error);
      return null;
    }
  }

  async function initStorefrontCart() {
    if (!serverCartEnabled) return;
    const cartId = getServerCartId();
    if (!cartId) return;
    const cart = state.storefrontCart || await fetchStorefrontCart(cartId);
    if (cart) {
      state.storefrontCart = cart;
      state.lastStorefrontTotal = cart.totalQuantity ?? null;
      state.lastStorefrontLineIds = extractCartLineIds(cart);
      renderStorefrontCart(cart);
    }
  }

  function extractCartLineIds(cart) {
    const edges = cart?.lines?.edges || [];
    return edges.map((edge) => edge.node?.id).filter(Boolean);
  }

  function didCartChange(cart) {
    if (!cart) return true;
    const total = cart.totalQuantity ?? null;
    const lineIds = extractCartLineIds(cart);
    if (state.lastStorefrontTotal === null) return true;
    if (total !== state.lastStorefrontTotal) return true;
    if (!state.lastStorefrontLineIds) return true;
    if (lineIds.length !== state.lastStorefrontLineIds.length) return true;
    return lineIds.some((id, idx) => id !== state.lastStorefrontLineIds[idx]);
  }

  async function resetCartIdFromThemeCart() {
    if (state.cartResetInFlight) return;
    const now = Date.now();
    if (now - state.lastCartResetAt < CART_RESET_COOLDOWN_MS) {
      setCartResetDebug('cooldown');
      return false;
    }
    state.cartResetInFlight = true;
    state.lastCartResetAt = now;
    try {
      const snapshot = getThemeCartSnapshot();
      const useCached = snapshot && snapshot.cart && (Date.now() - (snapshot.savedAt || 0) <= THEME_CART_SNAPSHOT_TTL_MS);
      const cached = useCached ? snapshot.cart : null;
      const data = cached || await (async () => {
        const response = await fetch('/cart.js', {
          headers: { 'Accept': 'application/json' },
          cache: 'no-store'
        });
        const contentType = response.headers.get('content-type') || '';
        const responseText = await response.text();
        try {
          const parsed = JSON.parse(responseText);
          setCartResetDebug('cart_js_ok', { contentType });
          saveThemeCartSnapshot(parsed);
          return parsed;
        } catch (error) {
          setCartResetDebug('cart_js_not_json', {
            contentType,
            challenge: detectChallengeResponse(contentType, responseText)
          });
          if (snapshot?.cart) {
            setCartResetDebug('cart_js_fallback_cache', { ageMs: Date.now() - (snapshot.savedAt || 0) });
            return snapshot.cart;
          }
          return null;
        }
      })();
      if (!data) return false;
      const items = Array.isArray(data?.items) ? data.items : [];
      const lines = items.map((item) => ({
        variantId: item?.variant_id,
        quantity: item?.quantity || 1
      })).filter((line) => line.variantId);
      const prevCartId = getServerCartId();
      setServerCartId(null);
      const result = await addToServerCart(lines, { forceNew: true, skipThemeSync: true });
      if (result?.data?.cart) {
        state.storefrontCart = result.data.cart;
        state.lastStorefrontTotal = result.data.cart.totalQuantity ?? null;
        state.lastStorefrontLineIds = extractCartLineIds(result.data.cart);
        if (state.cartAdapter === 'storefront') {
          renderStorefrontCart(result.data.cart);
        }
        const nextCartId = getServerCartId();
        const changed = Boolean(nextCartId && nextCartId !== prevCartId);
        setCartResetDebug(changed ? 'success' : 'cart_id_unchanged', {
          prevCartId,
          nextCartId
        });
        return changed;
      }
      setCartResetDebug('no_cart_returned');
    } catch (error) {
      console.warn('[ScanCamera] Cart reset failed', error);
      setCartResetDebug('exception', String(error?.message || error));
    } finally {
      state.cartResetInFlight = false;
    }
    return false;
  }

  function toVariantGid(variantId) {
    if (typeof variantId === 'string' && variantId.includes('gid://')) {
      return variantId;
    }
    const id = normalizeVariantId(variantId);
    return `gid://shopify/ProductVariant/${id}`;
  }

  function getCartIconBubbleEl() {
    let cartIconBubble = document.getElementById('cart-icon-bubble');
    if (cartIconBubble) return cartIconBubble;
    const cartIcon = document.getElementById('cart-icon');
    if (!cartIcon) return null;
    cartIconBubble = document.createElement('div');
    cartIconBubble.id = 'cart-icon-bubble';
    cartIcon.appendChild(cartIconBubble);
    return cartIconBubble;
  }

  function updateCartIconCount(totalQuantity) {
    const cartIconBubble = getCartIconBubbleEl();
    if (!cartIconBubble) return;
    const count = Number(totalQuantity);
    if (!Number.isFinite(count)) return;
    if (count <= 0) {
      if (state.open && state.lastKnownCartCount) {
        return;
      }
      cartIconBubble.innerHTML = '';
      state.lastKnownCartCount = 0;
      return;
    }
    state.lastKnownCartCount = count;
    const countLabel = count < 10000 ? `<span aria-hidden="true" class="header__cart-count-number">${count}</span>` : '';
    cartIconBubble.innerHTML = `
      <div class="header__cart-count absolute bg-theme-text text-theme-bg text-center">
        ${countLabel}
        <span class="visually-hidden">Cart items: ${count}</span>
      </div>
    `;
  }

  function updateCartUiFromResponse(data) {
    if (!data) return false;
    if (window.ProductForm && typeof window.ProductForm.updateCartIcon === 'function') {
      try {
        if (data?.sections?.['cart-icon-bubble']) {
          window.ProductForm.updateCartIcon(data);
          return true;
        }
      } catch (error) {
        console.warn('[ScanCamera] Cart icon update failed', error);
      }
    }
    if (typeof data?.item_count === 'number') {
      updateCartIconCount(data.item_count);
      return true;
    }
    try {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    } catch (error) {
      console.warn('[ScanCamera] Cart event failed', error);
    }
    return false;
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatMoney(amount, currencyCode) {
    const numeric = Number(amount);
    if (!Number.isFinite(numeric)) return '';
    const code = currencyCode || 'AUD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).format(numeric);
    } catch (error) {
      return `${code} ${numeric.toFixed(2)}`;
    }
  }

  function getCartDrawerContentEl() {
    return document.querySelector('#cart-drawer .cart-drawer__content');
  }

  function ensureStorefrontDrawerContainer() {
    const contentEl = getCartDrawerContentEl();
    if (!contentEl) return null;
    if (!state.drawerContainer) {
      const container = document.createElement('div');
      container.className = 'scan-camera-cart';
      state.drawerContainer = container;
      const summaryEl = document.querySelector('#cart-drawer .cart-drawer__summary');
      if (summaryEl) {
        state.drawerSummaryEl = summaryEl;
        summaryEl.dataset.scanHidden = 'true';
        summaryEl.style.display = 'none';
      }
      const children = Array.from(contentEl.children);
      state.drawerHiddenChildren = children.filter((child) => child !== container);
      state.drawerHiddenChildren.forEach((child) => {
        child.dataset.scanHidden = 'true';
        child.style.display = 'none';
      });
      contentEl.appendChild(container);
    }
    return state.drawerContainer;
  }

  function restoreThemeDrawer() {
    if (state.drawerHiddenChildren) {
      state.drawerHiddenChildren.forEach((child) => {
        child.style.display = '';
        delete child.dataset.scanHidden;
      });
    }
    state.drawerHiddenChildren = null;
    if (state.drawerSummaryEl) {
      state.drawerSummaryEl.style.display = '';
      delete state.drawerSummaryEl.dataset.scanHidden;
      state.drawerSummaryEl = null;
    }
    if (state.drawerContainer) {
      state.drawerContainer.remove();
      state.drawerContainer = null;
    }
  }

  function renderStorefrontCart(cart) {
    if (!cart) return;
    updateCartIconCount(cart.totalQuantity || 0);
    const container = ensureStorefrontDrawerContainer();
    if (!container) return;

    const lines = (cart.lines && cart.lines.edges) ? cart.lines.edges : [];
    if (!lines.length) {
      container.innerHTML = '<p class="text-sm text-center">Your cart is empty.</p>';
      return;
    }

    const itemsHtml = lines.map((edge) => {
      const line = edge.node || {};
      const merchandise = line.merchandise || {};
      const product = merchandise.product || {};
      const image = merchandise.image || {};
      const imageTag = image.url
        ? `<img src="${escapeHtml(image.url)}" alt="${escapeHtml(image.altText || product.title || merchandise.title)}" style="width:48px;height:48px;object-fit:cover;border-radius:6px;" />`
        : '';
      const title = escapeHtml(product.title || merchandise.title || 'Item');
      const variantTitle = merchandise.title && product.title && merchandise.title !== product.title
        ? `<div class="text-xs opacity-70">${escapeHtml(merchandise.title)}</div>`
        : '';
      const qty = Number(line.quantity || 0);
      const price = line.cost?.totalAmount;
      const priceText = price ? formatMoney(price.amount, price.currencyCode) : '';

      return `
        <div class="flex items-center gap-x-theme py-2 border-b border-opacity-20">
          <div>${imageTag}</div>
          <div class="flex-auto">
            <div class="text-sm font-semibold">${title}</div>
            ${variantTitle}
            <div class="text-xs opacity-70">Qty: ${qty}</div>
          </div>
          <div class="text-sm font-semibold">${escapeHtml(priceText)}</div>
        </div>
      `;
    }).join('');

    const subtotal = cart.cost?.subtotalAmount;
    const total = cart.cost?.totalAmount;
    const subtotalText = subtotal ? formatMoney(subtotal.amount, subtotal.currencyCode) : '';
    const totalText = total ? formatMoney(total.amount, total.currencyCode) : '';

    container.innerHTML = `
      <div class="scan-camera-cart-items">${itemsHtml}</div>
      <div class="scan-camera-cart-summary mt-3 text-sm">
        <div class="flex justify-between"><span>Subtotal</span><span>${escapeHtml(subtotalText)}</span></div>
        <div class="flex justify-between font-semibold mt-1"><span>Total</span><span>${escapeHtml(totalText)}</span></div>
      </div>
      <div class="scan-camera-cart-actions mt-3">
        <a class="btn btn--primary w-full" href="/cart">View cart</a>
      </div>
    `;
  }

  function mergeThemeSyncItems(items) {
    const merged = new Map();
    items.forEach((item) => {
      const propKey = serializeProperties(item.properties);
      const key = `${String(item.variantId)}::${propKey}`;
      const current = merged.get(key) || {
        variantId: item.variantId,
        quantity: 0,
        properties: item.properties,
        moq: item.moq || 1
      };
      current.quantity += Number(item.quantity || 0);
      current.moq = Math.max(Number(current.moq || 1), Number(item.moq || 1));
      merged.set(key, current);
    });
    return Array.from(merged.values()).filter((item) => item.quantity > 0);
  }

  function enqueueThemeSync(lines) {
    if (!Array.isArray(lines) || lines.length === 0) return;
    lines.forEach((line) => {
      const properties = getThemeCartPropertiesForVariant(line.variantId);
      state.pendingThemeSync.push({
        variantId: line.variantId,
        quantity: normalizeQuantityByMoq(line.quantity || 1, line.moq || 1),
        properties
      });
    });
    scheduleThemeSync();
  }

  function scheduleThemeSync() {
    const now = Date.now();
    const sinceLast = now - (state.lastThemeSyncAt || 0);
    if (sinceLast >= THEME_SYNC_DEBOUNCE_MS && !state.themeSyncInFlight) {
      state.lastThemeSyncAt = now;
      flushThemeSync();
      return;
    }
    if (state.themeSyncTimer) {
      clearTimeout(state.themeSyncTimer);
    }
    state.themeSyncTimer = setTimeout(() => {
      state.themeSyncTimer = null;
      flushThemeSync();
    }, THEME_SYNC_DEBOUNCE_MS);
  }

  async function flushThemeSync() {
    if (!state.pendingThemeSync.length || state.themeSyncInFlight) return;
    state.lastThemeSyncAt = Date.now();
    if (state.batchInFlight) {
      scheduleThemeSync();
      return;
    }
    state.themeSyncInFlight = true;
    const batch = mergeThemeSyncItems(state.pendingThemeSync.splice(0, state.pendingThemeSync.length));
    try {
      await waitForCartSlot({ themeSync: true });
      await addToCartBatchAjax(batch, { ignoreSecurityChallenge: true, assumeSuccessOnInvalidJson: true });
    } catch (error) {
      state.pendingThemeSync = batch.concat(state.pendingThemeSync);
    } finally {
      state.themeSyncInFlight = false;
    }
  }

  function setCartAdapter(name) {
    state.cartAdapter = name;
    if (name === 'theme') {
      restoreThemeDrawer();
    }
  }

  async function addToServerCart(lines, options = {}) {
    const serverCartUrl = getServerCartUrl();
    if (!serverCartUrl) {
      throw new Error('Server cart endpoint missing');
    }
    const payload = {
      cartId: options.forceNew ? null : getServerCartId(),
      lines: lines.map((line) => ({
        merchandiseId: toVariantGid(line.variantId),
        quantity: normalizeQuantityByMoq(line.quantity || 1, line.moq || 1)
      }))
    };
    const requestUrl = shopDomain ? `${serverCartUrl}?shop=${encodeURIComponent(shopDomain)}` : serverCartUrl;
    const response = await fetch(requestUrl, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const contentType = response.headers.get('content-type') || '';
    const responseText = await response.text();
    let data;
    try {
      if (!contentType.includes('application/json')) {
        throw new Error('Server cart returned invalid JSON');
      }
      data = JSON.parse(responseText);
    } catch (error) {
      setBackendDebug(responseText || String(error));
      throw new Error('Server cart returned invalid JSON');
    }

    if (!response.ok || !data?.ok) {
      setBackendDebug(data);
      if (data?.error === 'CART_NOT_FOUND') {
        setServerCartId(null);
      }
      throw new Error(data?.error || 'Server cart add failed');
    }

    if (data?.cartId) {
      setServerCartId(data.cartId);
      setBackendDebug({ cartId: data.cartId, totalQuantity: data?.cart?.totalQuantity });
      const storedId = getServerCartId();
      if (storedId && storedId !== data.cartId) {
        setServerCartId(data.cartId);
      }
    }
    setBackendDebug(data);
    if (data?.cart) {
      if (state.open && state.cartAdapter !== 'storefront' && getServerCartId()) {
        setCartAdapter('storefront');
      }
      state.storefrontCart = data.cart;
      state.lastStorefrontTotal = data.cart.totalQuantity ?? null;
      state.lastStorefrontLineIds = extractCartLineIds(data.cart);
      if (state.cartAdapter === 'storefront') {
        renderStorefrontCart(data.cart);
      }
    }
    if (!options.skipThemeSync && Array.isArray(lines) && lines.length > 0) {
      enqueueThemeSync(lines);
    }
    state.lastCartAddAt = Date.now();
    return { serverCart: true, data };
  }

  async function addToCartAjax(variantId, quantity, options = {}) {
    const id = normalizeVariantId(variantId);
    const normalizedQuantity = normalizeQuantityByMoq(quantity || 1, options.moq || 1);
    const formData = new FormData();
    formData.append('id', id.toString());
    formData.append('quantity', String(normalizedQuantity));
    if (options.properties) {
      Object.entries(options.properties).forEach(([key, value]) => {
        if (key) {
          formData.append(`properties[${key}]`, String(value));
        }
      });
    }

    const cartAddUrl = (window.theme?.routes?.cartAdd && window.theme.routes.cartAdd.endsWith('.js'))
      ? window.theme.routes.cartAdd
      : '/cart/add.js';

    const sectionsPayload = options.requestSections
      ? {
        sections: 'cart-icon-bubble',
        sections_url: window.location.pathname
      }
      : null;

    if (sectionsPayload) {
      formData.append('sections', sectionsPayload.sections);
      formData.append('sections_url', sectionsPayload.sections_url);
    }

    const requestUrl = options.requestSections
      ? `${cartAddUrl}?sections=cart-icon-bubble&sections_url=${encodeURIComponent(window.location.pathname)}`
      : cartAddUrl;

    const response = await fetch(requestUrl, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    });

    const contentType = response.headers.get('content-type') || '';
    const responseText = await response.text();
    let data;
    try {
      if (!contentType.includes('application/json')) {
        if (!options.ignoreSecurityChallenge) {
          triggerSecurityChallenge();
          throw new Error('SECURITY_CHALLENGE');
        }
        throw new Error('SYNC_CHALLENGE');
      }
      data = JSON.parse(responseText);
    } catch (error) {
      if (String(error?.message || '') === 'SECURITY_CHALLENGE') {
        throw error;
      }
      throw new Error('Cart add returned invalid JSON');
    }

    if (!response.ok || data.status === 422) {
      throw new Error(`${response.status || ''} ${data.description || data.message || 'Failed to add to cart'}`.trim());
    }

    state.lastCartAddAt = Date.now();
    scheduleCartRefresh();

    updateCartUiFromResponse(data);

    return { serverCart: false, data };
  }

  async function addToCart(variantId, quantity, moq = 1) {
    const normalizedQuantity = normalizeQuantityByMoq(quantity || 1, moq || 1);
    if (serverCartEnabled) {
      try {
        return await addToServerCart([{ variantId, quantity: normalizedQuantity, moq }]);
      } catch (error) {
        console.warn('[ScanCamera] Server cart add failed, falling back', error);
      }
    }
    return addToCartAjax(variantId, normalizedQuantity, { ignoreSecurityChallenge: serverCartEnabled, moq });
  }

  async function addToCartBatchAjax(items, options = {}) {
    const payload = {
      items: items.map((item) => {
        const normalizedQuantity = normalizeQuantityByMoq(item.quantity || 1, item.moq || 1);
        const entry = {
          id: normalizeVariantId(item.variantId),
          quantity: normalizedQuantity
        };
        if (item.properties) {
          entry.properties = item.properties;
        }
        return entry;
      })
    };
    const cartAddUrl = (window.theme?.routes?.cartAdd && window.theme.routes.cartAdd.endsWith('.js'))
      ? window.theme.routes.cartAdd
      : '/cart/add.js';
    const requestUrl = options.requestSections
      ? `${cartAddUrl}?sections=cart-icon-bubble&sections_url=${encodeURIComponent(window.location.pathname)}`
      : cartAddUrl;

    const response = await fetch(requestUrl, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload)
    });

    const contentType = response.headers.get('content-type') || '';
    const responseText = await response.text();
    let data;
    try {
      if (!contentType.includes('application/json')) {
        if (!options.ignoreSecurityChallenge) {
          triggerSecurityChallenge();
          throw new Error('SECURITY_CHALLENGE');
        }
        if (options.assumeSuccessOnInvalidJson) {
          setBackendDebug({ themeSync: 'assumed_success', status: response.status, contentType });
          state.lastCartAddAt = Date.now();
          return { serverCart: false, data: null, meta: { status: response.status, contentType, assumed: true } };
        }
        throw new Error('SYNC_CHALLENGE');
      }
      data = JSON.parse(responseText);
    } catch (error) {
      if (String(error?.message || '') === 'SECURITY_CHALLENGE') {
        throw error;
      }
      throw new Error('Cart add returned invalid JSON');
    }

    if (!response.ok || data.status === 422) {
      throw new Error(`${response.status || ''} ${data.description || data.message || 'Failed to add to cart'}`.trim());
    }

    state.lastCartAddAt = Date.now();
    return { serverCart: false, data, meta: { status: response.status, contentType } };
  }

  async function addToCartBatch(items) {
    const normalizedItems = Array.isArray(items)
      ? items.map((item) => ({
        ...item,
        quantity: normalizeQuantityByMoq(item.quantity || 1, item.moq || 1)
      }))
      : [];
    if (serverCartEnabled) {
      try {
        return await addToServerCart(normalizedItems);
      } catch (error) {
        console.warn('[ScanCamera] Server cart batch failed, falling back', error);
      }
    }
    return addToCartBatchAjax(normalizedItems, { ignoreSecurityChallenge: serverCartEnabled });
  }

  function normalizeVariantId(variantId) {
    let numericVariantId = variantId;
    if (typeof variantId === 'string' && variantId.includes('/')) {
      numericVariantId = variantId.split('/').pop();
    }
    const id = parseInt(numericVariantId, 10);
    if (isNaN(id) || id <= 0) {
      throw new Error('Invalid variant ID');
    }
    return id;
  }

  function triggerSecurityChallenge() {
    state.securityHold = true;
  }

  function showSecurityBanner() {
    if (state.securityBanner) return;
    const banner = document.createElement('button');
    banner.type = 'button';
    banner.className = 'scan-camera-security';
    banner.textContent = 'Security check triggered — tap to continue';
    banner.addEventListener('click', () => {
      persistResumeState();
      window.location.href = '/cart?scan_return=1';
    });
    document.body.appendChild(banner);
    state.securityBanner = banner;
  }

  function triggerSuccessFeedback() {
    flashOverlay();
    playBeep();
  }

  function flashOverlay() {
    if (!flashEl) return;
    flashEl.classList.add('scan-camera-flash--active');
    setTimeout(() => {
      flashEl.classList.remove('scan-camera-flash--active');
    }, 140);
  }

  function primeAudio() {
    if (state.audioCtx) return;
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      state.audioCtx = new AudioContext();
      if (state.audioCtx.state === 'suspended') {
        state.audioCtx.resume().catch(() => {});
      }
    } catch (error) {
      console.warn('[ScanCamera] Audio init failed', error);
    }
  }

  function playBeep() {
    if (!state.audioCtx) return;
    if (state.audioCtx.state === 'suspended') {
      state.audioCtx.resume().catch(() => {});
      return;
    }
    const oscillator = state.audioCtx.createOscillator();
    const gain = state.audioCtx.createGain();
    oscillator.type = 'square';
    oscillator.frequency.value = 1200;
    gain.gain.value = 0.05;
    oscillator.connect(gain);
    gain.connect(state.audioCtx.destination);
    oscillator.start();
    setTimeout(() => {
      oscillator.stop();
      oscillator.disconnect();
      gain.disconnect();
    }, 80);
  }

  function playErrorBeep() {
    if (!state.audioCtx) return;
    if (state.audioCtx.state === 'suspended') {
      state.audioCtx.resume().catch(() => {});
      return;
    }

    // Single, urgent beep: louder + longer than success beep.
    const ctx = state.audioCtx;
    const startAt = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    osc.connect(gain);
    gain.connect(ctx.destination);

    const attack = 0.01;
    const release = 0.06;
    const dur = 0.42;
    const peak = 0.14;

    // Pitch chosen to be attention-grabbing but distinct from success (1200Hz).
    osc.frequency.setValueAtTime(620, startAt);

    // Start nearly silent to avoid clicks, then ramp up/down.
    gain.gain.setValueAtTime(0.0001, startAt);
    gain.gain.exponentialRampToValueAtTime(peak, startAt + attack);
    gain.gain.setValueAtTime(peak, startAt + dur - release);
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + dur);

    osc.start(startAt);
    osc.stop(startAt + dur + 0.02);

    setTimeout(() => {
      try {
        osc.disconnect();
        gain.disconnect();
      } catch (error) {
        // ignore
      }
    }, 600);
  }

  function loadScript(url) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Script load failed'));
      document.head.appendChild(script);
    });
  }

  function submitWedgeBarcode(raw) {
    const normalized = normalizeCode(raw);
    if (!normalized) return;
    const digits = sanitizeBarcode(normalized);
    let candidateCodes = [];
    if (digits.length === 12) {
      candidateCodes = buildLookupCodes(normalized, digits, 'upc_a');
    } else if (digits.length === 13) {
      candidateCodes = buildLookupCodes(normalized, digits, 'ean_13');
    } else if (digits.length === 8) {
      candidateCodes = buildLookupCodes(normalized, digits, 'ean_8');
    } else if (digits.length === 14) {
      candidateCodes = buildLookupCodes(normalized, digits, 'itf_14');
    } else {
      candidateCodes = [normalized];
    }
    if (!candidateCodes.length) {
      showToast('Invalid barcode', 'error');
      return;
    }
    enqueueScan(candidateCodes);
  }

  function bindEvents() {
    if (button) {
      button.addEventListener('click', () => {
        if (!state.enabled) return;
        openOverlay();
      });
    }
    if (closeBtn) {
      closeBtn.addEventListener('click', closeOverlay);
    }
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) closeOverlay();
    });
    document.addEventListener(
      'click',
      (event) => {
        if (!state.open) return;
        if (overlay.contains(event.target)) return;
        const closeTrigger = event.target.closest?.(
          '.header__account-menu, #cart-icon, .main-menu'
        );
        if (!closeTrigger) return;
        closeOverlay();
      },
      true
    );
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && state.open) {
        closeOverlay();
      }
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible' && state.open) {
        stopDecodeLoop();
      } else if (state.open) {
        startDecodeLoop();
        if (state.securityHold) {
          state.securityHold = false;
          processQueue();
          flushCartBatch();
        }
      }
    });
  }

  function init() {
    if (window.location.pathname.startsWith('/cart')) {
      const params = new URLSearchParams(window.location.search);
      if (params.get('scan_return') === '1') {
        loadCache();
        bindEvents();
        initStoreHeaderHeightSync();
        setEnabled(true);
        restoreResumeState();
        openOverlay();
        return;
      }
    }
    loadCache();
    bindEvents();
    initStoreHeaderHeightSync();
    setEnabled(true);
    restoreResumeState();
    if (isScanDebugEnabled()) {
      updateScanDebugPanel();
    }
  }

  window.__isalbiScanCartApi = {
    submitWedgeBarcode,
    enqueueScan,
    processQueue,
    flushCartBatch,
    getSessionStore: () => sessionStore,
    isServerCartEnabled: () => serverCartEnabled
  };
  window.scanCamera = {
    setEnabled,
    openOverlay,
    closeOverlay,
    submitWedgeBarcode,
    refreshScanDebug: updateScanDebugPanel,
    isScanDebugEnabled
  };
  try {
    window.dispatchEvent(new CustomEvent('isalbi:scan-cart-api-ready'));
  } catch (e) {}

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
